import Vue from "vue";
import { reflectKeys, jwt, appLocalStorage } from '@/app/shared/services';

import { fetchGuide, fetchTags, fetchArtefacts, fetchArtefact, fetchArtefactUnit } from '../services';

/** Initial state */
const initialState = {
  apiCallStatusTrack: {
    guide: false,
    tags: false,
    artefacts: false,
    artefact: false,
    artefactUnit: false,
    payload: false
  },
  data: null,
  error: null,
  guide: [],
  tags: [],
  artefacts: [],
  artefact: {},
  artefactUnit: {}
};

/** Prefix for mutation types and actiontypes */
const namespacedPrefix = '[COMPETER]';

/**
 * Mutation types
 */
const mutationTypes = reflectKeys(
  [
    'COMPETER_DATA_REQUEST',
    'COMPETER_DATA_SUCCESS',
    'COMPETER_DATA_ERROR',
    'COMPETER_DATA_RESET',
    'LOOKUP_COMPETER_GUIDE',
    'SET_COMPETER_GUIDE',
    'SET_COMPETER_GUIDE_ON',
    'LOOKUP_COMPETER_TAGS',
    'SET_COMPETER_TAGS',
    'LOOKUP_COMPETER_ARTEFACTS',
    'SET_COMPETER_ARTEFACTS',
    'LOOKUP_COMPETER_ARTEFACT',
    'SET_COMPETER_ARTEFACT',
    'LOOKUP_COMPETER_ARTEFACT_UNIT',
    'SET_COMPETER_ARTEFACT_UNIT',
    'LOOKUP_COMPETER_PAYLOAD',
    'SET_COMPETER_PAYLOAD',
    'RESET_COMPETER'
  ],
  namespacedPrefix
);

const {
  COMPETER_DATA_REQUEST,
  COMPETER_DATA_SUCCESS,
  COMPETER_DATA_ERROR,
  COMPETER_DATA_RESET,
  LOOKUP_COMPETER_GUIDE,
  SET_COMPETER_GUIDE,
  SET_COMPETER_GUIDE_ON,
  LOOKUP_COMPETER_TAGS,
  SET_COMPETER_TAGS,
  LOOKUP_COMPETER_ARTEFACTS,
  SET_COMPETER_ARTEFACTS,
  LOOKUP_COMPETER_ARTEFACT,
  SET_COMPETER_ARTEFACT,
  LOOKUP_COMPETER_ARTEFACT_UNIT,
  SET_COMPETER_ARTEFACT_UNIT,
  LOOKUP_COMPETER_PAYLOAD,
  SET_COMPETER_PAYLOAD,
  RESET_COMPETER
} = mutationTypes;

/**
 * Auth data mutations
 */
const mutations = {
  /** auth data request */
  [COMPETER_DATA_REQUEST](state) {
    Object.assign(state, { error: null });
  },

  /** auth data success */
  [COMPETER_DATA_SUCCESS](state, payload) {
    Object.assign(state, { apiCallStatusTrack: initialState.apiCallStatusTrack, data: payload, user: payload, isAuthenticated: true });
  },

  /** auth data error */
  [COMPETER_DATA_ERROR](state, payload) {
    Object.assign(state, {
      apiCallStatusTrack: initialState.apiCallStatusTrack,
      data: null,
      error: payload || true
    });
  },

  /** reset auth data */
  [COMPETER_DATA_RESET](state) {
    Object.assign(state, initialState);
  },

  [LOOKUP_COMPETER_GUIDE](state) {
    state.apiCallStatusTrack.guide = true;
  },
  [SET_COMPETER_GUIDE](state, payload) {
    state.guide = payload.res.data;
    state.apiCallStatusTrack.guide = false;
  },
  [SET_COMPETER_GUIDE_ON](state, guide) {
    state.guideOn = guide;
  },
  [LOOKUP_COMPETER_TAGS](state) {
    state.apiCallStatusTrack.tags = true;
  },
  [SET_COMPETER_TAGS](state, payload) {
    state.tags = payload.res.data;
    state.tagsCount = payload.res.data ? payload.res.data.length : 0;
    state.apiCallStatusTrack.tags = false;
  },
  [LOOKUP_COMPETER_ARTEFACTS](state) {
    state.apiCallStatusTrack.artefacts = true;
  },
  [SET_COMPETER_ARTEFACTS](state, payload) {
    state.artefacts = payload.artefacts;
    state.artefactsCount = payload.artefacts ? payload.artefacts.length : 0;
    state.apiCallStatusTrack.artefacts = false;
  },
  [LOOKUP_COMPETER_ARTEFACT](state) {
    state.apiCallStatusTrack.artefact = true;
  },
  [SET_COMPETER_ARTEFACT](state, payload) {
    state.artefact = {};
    state.artefact = payload;
    state.apiCallStatusTrack.artefact = false;
  },
  [LOOKUP_COMPETER_ARTEFACT_UNIT](state) {
    state.apiCallStatusTrack.artefactUnit = true;
  },
  [SET_COMPETER_ARTEFACT_UNIT](state, payload) {
    state.artefactUnit = payload;
    state.apiCallStatusTrack.artefactUnit = false;
  },
  [LOOKUP_COMPETER_PAYLOAD](state) {
    state.apiCallStatusTrack.payload = true;
  },
  [SET_COMPETER_PAYLOAD](state, payload) {
    state.artefact = payload;
    state.apiCallStatusTrack.payload = false;
  },
  [RESET_COMPETER](state) {
    for (let f in state) {
      Vue.set(state, f, state.initialState[f]);
    }
  }

};

/** Actions types constants */
export const actionsTypes = reflectKeys([
  'FETCH_COMPETER_GUIDE',
  'FETCH_COMPETER_TAGS',
  'FETCH_COMPETER_ARTEFACTS',
  'FETCH_COMPETER_ARTEFACT',
  'FETCH_COMPETER_ARTEFACT_UNIT',
  'FETCH_COMPETER_PAYLOAD',
  'RESET_COMPETER_STATE'
], namespacedPrefix);

/**
 * Users data actions
 */
const actions = {

  async [actionsTypes.FETCH_COMPETER_GUIDE](context, payload) {
    context.commit(LOOKUP_COMPETER_GUIDE);
    return await fetchGuide(payload)
      .then(data => {
        payload.res = data;
        context.commit(SET_COMPETER_GUIDE, payload);
      })
      .catch(error => {
        payload.res = error;
        context.commit(SET_COMPETER_GUIDE, payload);
      });
  },
  async [actionsTypes.FETCH_COMPETER_TAGS]({ commit }, payload) {
    commit(LOOKUP_COMPETER_TAGS);
    return await fetchTags(payload)
      .then(data => {
        payload.res = data;
        commit(SET_COMPETER_TAGS, payload);
      })
      .catch(error => {
        payload.res = error;
        commit(SET_COMPETER_TAGS, payload);
      });
  },
  async [actionsTypes.FETCH_COMPETER_ARTEFACTS]({ commit }, payload) {
    commit(LOOKUP_COMPETER_ARTEFACTS);
    return await fetchArtefacts(payload)
      .then(data => {
        payload.res = data;
        commit(SET_COMPETER_ARTEFACTS, payload);
      })
      .catch(error => {
        payload.res = error;
        commit(SET_COMPETER_ARTEFACTS, payload);
      });
  },
  async [actionsTypes.FETCH_COMPETER_ARTEFACT](context, payload, prevArtifact) {
    // avoid extronuous network call if search exists
    context.commit(LOOKUP_COMPETER_ARTEFACT);
    if (prevArtifact !== undefined) {
      return context.commit(SET_COMPETER_ARTEFACT, prevArtifact);
    }

    return await fetchArtefact(payload)
      .then(data => {
        payload.res = data.data;
        context.commit(SET_COMPETER_ARTEFACT, payload);
      })
      .catch(error => {
        payload.res = { error: 'No records found for the search criteria.' };
        context.commit(SET_COMPETER_ARTEFACT, payload);
        // throw new Error(error);
      });
  },
  async [actionsTypes.FETCH_COMPETER_ARTEFACT_UNIT](context, payload, prevArtifact) {
    // avoid extronuous network call if search exists
    context.commit(LOOKUP_COMPETER_ARTEFACT_UNIT);
    if (prevArtifact !== undefined) {
      return context.commit(SET_COMPETER_ARTEFACT_UNIT, prevArtifact);
    }
    return await fetchArtefactUnit(payload)
      .then(data => {
        payload.res = data;
        context.commit(SET_COMPETER_ARTEFACT_UNIT, payload);
      })
      .catch(error => {
        payload.res = error;
        context.commit(SET_COMPETER_ARTEFACT_UNIT, payload);
      });
  },
  async [actionsTypes.FETCH_COMPETER_PAYLOAD](context, payload, prevArtifact) {
    // avoid extronuous network call if search exists
    context.commit(LOOKUP_COMPETER_PAYLOAD);
    if (prevArtifact !== undefined) {
      return context.commit(SET_COMPETER_PAYLOAD, prevArtifact);
    }

    context.commit(SET_COMPETER_PAYLOAD, payload);
    return await payload;
  },

  [actionsTypes.RESET_COMPETER_STATE]({ commit }) {
    commit(RESET_COMPETER);
  }
};

/**
 * Users data actions
 */
const state = {
  ...initialState,
  guideOn: [],
  loading: true,
  tagsCount: 0,
  artefactsCount: 0
};

const getters = {
  isLoading(state) {
    state.loading = false;
    for (let x in state.apiCallStatusTrack) {
      if(state.apiCallStatusTrack[x]) {
        state.loading = true;
        break;
      }
    }
    return state.loading;
  },
  tagsCount(state) {
    return state.tagsCount;
  },
  tags(state) {
    return state.tags;
  },
  artefactsCount(state) {
    return state.artefactsCount;
  },
  artefacts(state) {
    return state.artefacts;
  },
  artefact(state) {
    return state.artefact;
  },
  artefactUnit(state) {
    return state.artefactUnit;
  },
  guide(state) {
    return state.guide;
  },
  guideOn(state) {
    return state.guideOn;
  }
};

export default {
  mutations,
  actions,
  state,
  getters
};
